import { createFileRoute, retainSearchParams } from "@tanstack/react-router";
import { VisitDetailPage } from "@/components/features/WorkOrders/VisitDetail/VisitDetailPage";
import { authGuardWithPermissions } from "@/utils/authGuard";
import {
  leaveRecordTenant,
  recordTenantSearchSchema,
  syncRecordTenant,
} from "@/utils/recordTenantRoute";
import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { useGetWorkOrderHeaderData } from "@/hooks/work-orders/details/useOverviewQueries";
import { useVisitDetail } from "@/hooks/work-orders/visit/useVisitDetail";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { useMemo } from "react";

export const Route = createFileRoute("/_authenticated/work-orders/$workOrderId/$visitId/")({
  component: VisitDetail,
  validateSearch: recordTenantSearchSchema.parse,
  search: { middlewares: [retainSearchParams(["tenant"])] },
  onEnter: syncRecordTenant,
  onStay: syncRecordTenant,
  onLeave: leaveRecordTenant,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function VisitDetail() {
  const { visitId, workOrderId } = Route.useParams();
  const { data: workOrderHeaderData } = useGetWorkOrderHeaderData(workOrderId);
  const { data: visitDetail } = useVisitDetail(visitId);

  const visitBreadcrumbTitle = useMemo(() => {
    if (!visitDetail) return "Loading...";
    return `Visit ${visitDetail.visitNumber} - ${visitDetail.visitType}`;
  }, [visitDetail]);

  const breadcrumbItems = useMemo(
    () => [
      {
        title: "Work Orders",
        href: "/work-orders",
        icon: getRouteIcon(MODULE_KEYS_MAP.workOrders),
      },
      {
        title: workOrderHeaderData?.data?.workOrderNumber || "Loading...",
        href: `/work-orders/${workOrderId}`,
      },
      {
        title: visitBreadcrumbTitle,
        href: `/work-orders/${workOrderId}/${visitId}`,
      },
    ],
    [workOrderHeaderData?.data?.workOrderNumber, workOrderId, visitId, visitBreadcrumbTitle],
  );

  useBreadcrumb(breadcrumbItems, { listView: "List View" });

  return <VisitDetailPage visitId={visitId} workOrderId={workOrderId} />;
}
